import { useCallback, useEffect, useRef } from "react";

/** How long to wait for the element: past a refetch on a slow connection, short of surprising anyone. */
const WAIT_MS = 5000;

/**
 * Focus follows an item that moves (issue #285). Joining a group moves its card from "Find a group" to "My groups";
 * answering a serving date moves it from "Waiting" to "You are on". The button pressed is gone once the data comes
 * back, so focus fell to the page and a keyboard user started again from the top. Call it with a selector for the
 * item's control in its new place: it is focused as soon as it exists, and given up after a few seconds if it never
 * does. The selector must match only the new place (a data attribute for the item's new state), not the old one.
 * For a removal, pass `gone`: the removed item's selector. Focus moves to what comes next only once it has left the
 * page, so neither the dialog's own return of focus nor the re-render undoes it.
 */
export interface FocusWhenReadyOptions {
  /** Wait until nothing matches this selector, as well as for the target to exist. */
  readonly gone?: string;
}

export function useFocusWhenReady(): (selector: string, options?: FocusWhenReadyOptions) => void {
  const stop = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      stop.current?.();
    },
    [],
  );
  return useCallback((selector: string, options: FocusWhenReadyOptions = {}) => {
    stop.current?.();
    const tryFocus = (): boolean => {
      if (options.gone !== undefined && document.querySelector(options.gone) !== null) return false;
      const target = document.querySelector<HTMLElement>(selector);
      if (target === null) return false;
      target.focus();
      return true;
    };
    if (tryFocus()) return;
    const observer = new MutationObserver(() => {
      if (tryFocus()) done();
    });
    const timer = setTimeout(() => {
      done();
    }, WAIT_MS);
    function done() {
      observer.disconnect();
      clearTimeout(timer);
      stop.current = null;
    }
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    stop.current = done;
  }, []);
}
